import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingState } from '@/components/loading-state'

import type { PushPreferencesState } from '../hooks/use-push-preferences'
import { DEFAULT_PUSH_PREFERENCES } from '../hooks/use-push-preferences'
import { PUSH_PREFERENCE_KEYS } from '../types'
import { NotificationNotice } from './notice'

interface PushPreferencesProps {
  state: PushPreferencesState
}

/**
 * Six frozen push categories. A toggle saves a partial PATCH and never
 * suppresses the in-app row: it only changes whether the backend may deliver
 * that category through push (D-043).
 */
export function PushPreferences({ state }: PushPreferencesProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const listId = useId()
  const {
    preferences,
    phase,
    savingKey,
    saveErrorKey,
    retry,
    setPreference,
  } = state
  const values = preferences ?? DEFAULT_PUSH_PREFERENCES

  return (
    <section aria-labelledby={headingId} className="mt-4">
      <h2 id={headingId} className="text-lg">
        {t('notifications.push.preferencesTitle')}
      </h2>
      <p className="mt-1 max-w-[60ch] text-sm leading-relaxed text-muted">
        {t('notifications.push.preferencesBody')}
      </p>

      {saveErrorKey ? (
        <div className="mt-3">
          <NotificationNotice tone="error" messageKey={saveErrorKey} />
        </div>
      ) : null}

      {phase === 'loading' ? (
        <LoadingState labelKey="notifications.push.preferencesLoading" compact />
      ) : phase === 'error' ? (
        <div className="mt-3">
          <NotificationNotice
            tone="error"
            messageKey="notifications.push.preferencesError"
            onRetry={retry}
          />
        </div>
      ) : (
        <ul id={listId} className="mt-2 divide-y divide-line">
          {PUSH_PREFERENCE_KEYS.map((key) => {
            const checked = values[key]
            const labelId = `${listId}-${key}-label`
            const descriptionId = `${listId}-${key}-description`

            return (
              <li key={key} className="flex items-start gap-4 py-3">
                <div className="min-w-0 flex-1">
                  <span
                    id={labelId}
                    className="block text-[15px] font-bold text-deep"
                  >
                    {t(`notifications.push.categories.${key}.label`)}
                  </span>
                  <span
                    id={descriptionId}
                    className="mt-0.5 block text-sm leading-snug text-muted"
                  >
                    {t(`notifications.push.categories.${key}.description`)}
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={checked}
                  aria-labelledby={labelId}
                  aria-describedby={descriptionId}
                  aria-busy={savingKey === key}
                  disabled={savingKey !== null}
                  className="preference-switch"
                  onClick={() => void setPreference(key, !checked)}
                >
                  <span className="preference-switch__thumb" aria-hidden="true" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
